// Opt-in session renewal for the three Terminal Stamp routes only.
export function createTerminalSession({
  http, authHttp, router, storage, cookies, store, state, apiUrl, onExpired,
}) {
  const sessionKey = "TerminalStampSession";
  const terminalPaths = ["/TerminalStampMain", "/TerminalStamp", "/TerminalStampRabbit"];
  let pending = null;
  let unavailableToken = null;
  let expiredToken = null;
  const isActive = () => terminalPaths.includes(router.currentRoute.path);
  const accessToken = () => storage.getItem("Token");
  const loggedIn = () => storage.getItem("isLoggedIn") === "true";

  function expiry(token) {
    try {
      const value = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(value)).exp * 1000 || 0;
    } catch (_) {
      return 0;
    }
  }

  function readSession(token) {
    try {
      const session = JSON.parse(storage.getItem(sessionKey));
      return session && session.accessToken === token ? session : null;
    } catch (_) {
      return null;
    }
  }

  function cancelled() {
    const error = new Error("Terminal session changed");
    error.terminalSessionHandled = true;
    return error;
  }

  function expire(error) {
    error.terminalSessionHandled = true;
    if (!state.expired) {
      expiredToken = accessToken();
      state.expired = true;
      storage.removeItem(sessionKey);
      onExpired();
    }
    return error;
  }

  function checkCurrent(token) {
    if (!isActive() || !loggedIn() || accessToken() !== token) throw cancelled();
  }

  async function renew(token, force) {
    let session = readSession(token);
    if (!session) {
      storage.removeItem(sessionKey);
      if (expiry(token) <= Date.now()) throw expire(new Error("Session หมดอายุ"));
      // A server without the optional endpoint retains its existing behavior.
      if (unavailableToken === token) return token;
      let response;
      try {
        response = await authHttp.post(`${apiUrl}TerminalSession/Start`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        checkCurrent(token);
        if ([404, 405].includes(error.response?.status)) {
          unavailableToken = token;
          return token;
        }
        throw error;
      }
      checkCurrent(token);
      if (response.data?.status !== 0 || !response.data.data?.refreshToken) {
        throw expire(new Error("Session หมดอายุ"));
      }
      session = { ...response.data.data, accessToken: token, initialAccessToken: token };
      storage.setItem(sessionKey, JSON.stringify(session));
    }
    if (new Date(session.refreshExpiresAt).getTime() <= Date.now()) {
      throw expire(new Error("Session หมดอายุ"));
    }
    if (!force && expiry(token) > Date.now() + 60000) return token;

    const response = await authHttp.post(`${apiUrl}TerminalSession/Refresh`, {
      refreshToken: session.refreshToken,
    }, { headers: { Authorization: "" } });
    checkCurrent(token);
    const nextToken = response.data?.data?.token;
    if (response.data?.status !== 0 || !nextToken || expiry(nextToken) <= Date.now()) {
      throw expire(new Error("Session หมดอายุ"));
    }
    session.accessToken = nextToken;
    storage.setItem(sessionKey, JSON.stringify(session));
    storage.setItem("Token", nextToken);
    http.defaults.headers.common.Authorization = `Bearer ${nextToken}`;
    const expires = new Date(expiry(nextToken));
    cookies.set("Token", nextToken, expires);
    cookies.set("isLoggedIn", true, expires);
    for (const key of ["TenantID", "Username"]) {
      const value = cookies.get(key);
      if (value != null) cookies.set(key, value, expires);
    }
    store.commit("auth_success", nextToken);
    return nextToken;
  }

  function ensureFresh(force = false) {
    if (!isActive() || !loggedIn() || !accessToken()) return Promise.resolve(null);
    if (state.expired && accessToken() === expiredToken) return Promise.reject(cancelled());
    state.expired = false;
    if (pending) return pending;
    const token = accessToken();
    pending = renew(token, force).catch(error => {
      if (error.terminalSessionHandled) throw error;
      checkCurrent(token);
      if ([400, 401, 403].includes(error.response?.status)) throw expire(error);
      // Network errors and server outages must not clear a valid session.
      error.terminalSessionTransient = true;
      throw error;
    }).finally(() => { pending = null; });
    return pending;
  }

  function isProtectedRequest(config) {
    const url = new URL(config.url, config.baseURL || apiUrl);
    if (!url.href.startsWith(apiUrl)) return false;
    const endpoint = url.href.slice(apiUrl.length).split("?")[0];
    return !endpoint.startsWith("TerminalSession/") &&
      (!endpoint.startsWith("Login/") || [
        "Login/CheckToken", "Login/GetDataUserByToken", "Login/ChangePassword",
      ].includes(endpoint));
  }

  function checkRequestSession(config) {
    const token = accessToken();
    if (!loggedIn() || !token ||
        (readSession(token)?.initialAccessToken || token) !== config.terminalSessionOrigin) {
      throw cancelled();
    }
  }

  http.interceptors.request.use(async config => {
    if (!isActive() || !loggedIn() || !accessToken() || !isProtectedRequest(config)) return config;
    config.terminalSessionRequest = true;
    const token = await ensureFresh();
    config.headers.Authorization = `Bearer ${token}`;
    config.terminalSessionOrigin = readSession(token)?.initialAccessToken || token;
    if (["Login/CheckToken", "Login/GetDataUserByToken"].some(endpoint => config.url.endsWith(endpoint))) {
      config.data = { token };
    }
    return config;
  });

  http.interceptors.response.use(response => {
    const checkTokenRejected = response.config.url.endsWith("Login/CheckToken") &&
      response.data?.status !== 0 && response.data?.status !== 2;
    if (response.config.terminalSessionRequest && (response.data?.status === 4 || checkTokenRejected)) {
      checkRequestSession(response.config);
      if (response.config.headers.Authorization !== `Bearer ${accessToken()}`) throw cancelled();
      throw expire(new Error(response.data.message || "Session หมดอายุ"));
    }
    return response;
  }, async error => {
    const config = error.config;
    if (config?.terminalSessionRequest && error.response?.status === 401) checkRequestSession(config);
    if (error.terminalSessionHandled || error.terminalSessionTransient ||
        !config?.terminalSessionRequest || error.response?.status !== 401 || !isActive()) {
      throw error;
    }
    if (config.terminalSessionRetry) throw expire(error);
    const failedToken = config.headers.Authorization;
    // A concurrent response may arrive after another request has renewed the token.
    const token = failedToken !== `Bearer ${accessToken()}` ? accessToken() : await ensureFresh(true);
    checkCurrent(token);
    config.terminalSessionRetry = true;
    config.headers.Authorization = `Bearer ${token}`;
    return http.request(config);
  });

  return { isActive, ensureFresh, state };
}
